import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, AudioLines, Loader2, ChevronDown, ChevronRight, AlertTriangle, Scissors, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { fmtTime, pollJob } from "./snippetUtil";
import { ClipCard } from "./ClipCard";

function scoreColor(score) {
  if (score >= 80) return "text-emerald-300 border-emerald-500/40 bg-emerald-500/10";
  if (score >= 60) return "text-amber-300 border-amber-500/40 bg-amber-500/10";
  return "text-slate-300 border-slate-500/40 bg-slate-500/10";
}

export function MediaWorkbench({ media, clips, costs, templates, onRefreshMedia, onClipsChanged }) {
  const [transJob, setTransJob] = useState(null);
  const [hlJob, setHlJob] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [cut, setCut] = useState({ start: "", end: "", title: "" });
  const [cutting, setCutting] = useState(false);

  const transcribe = async () => {
    setTransJob({ progress: 0, total: 1 });
    try {
      const { data } = await api.post(`/snippets/media/${media.media_id}/transcribe`);
      const job = await pollJob(data.job_id, (j) => setTransJob({ progress: j.progress, total: j.total }));
      toast.success(`Transcribed — ${job.result?.segments || 0} segments, ${job.result?.words || 0} words`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Transcription failed");
    } finally {
      setTransJob(null);
      onRefreshMedia();
    }
  };

  const findHighlights = async () => {
    setHlJob(true);
    try {
      const { data } = await api.post(`/snippets/media/${media.media_id}/highlights`);
      const job = await pollJob(data.job_id);
      toast.success(`Found ${job.result?.found || 0} highlight${job.result?.found === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Highlight detection failed");
    } finally {
      setHlJob(false);
      onRefreshMedia();
    }
  };

  const createClip = async ({ start, end, title = "", hl_id = "" }) => {
    setCutting(true);
    try {
      await api.post("/snippets/clips", {
        media_id: media.media_id, start: Number(start), end: Number(end), title, hl_id,
        template: "radio_highlights",
      });
      toast.success("Clip cut — scroll down to style and render it");
      onClipsChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cut the clip");
    } finally {
      setCutting(false);
    }
  };

  const transcript = media.transcript || [];
  const highlights = media.highlights || [];
  const transcribed = media.status === "transcribed" || transcript.length > 0;
  const transcribing = media.status === "transcribing" || !!transJob;

  return (
    <div className="space-y-4" data-testid="snip-workbench">
      <div className="rounded-xl border border-line bg-bg-surface p-5">
        <div className="flex flex-wrap items-center gap-3 mb-1">
          <h3 className="type-section-heading text-ink-primary truncate flex-1 min-w-0" data-testid="snip-media-title">{media.filename}</h3>
          <span className="type-timestamp text-ink-muted">
            {media.kind === "video" ? "Video" : "Audio"} · {fmtTime(media.duration)}{media.language ? ` · ${media.language}` : ""}
          </span>
        </div>
        {media.flags?.music_suspected && (
          <div className="flex items-center gap-2 type-list-subtitle text-amber-300 mb-2" data-testid="snip-music-warning">
            <AlertTriangle size={13} /> This recording looks mostly musical — highlight detection works best on talk segments.
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={transcribe} disabled={transcribing}
            className="type-button-label flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-2 text-bg-base disabled:opacity-40"
            data-testid="snip-transcribe-btn">
            {transcribing ? <Loader2 size={13} className="animate-spin" /> : <AudioLines size={13} />}
            {transcribing
              ? `Transcribing${transJob?.total > 1 ? ` ${transJob.progress}/${transJob.total}` : "…"}`
              : `${transcribed ? "Re-transcribe" : "Transcribe"} · ${costs.transcribe} cr`}
          </button>
          <button onClick={findHighlights} disabled={!transcribed || hlJob || transcribing}
            className="type-button-label flex items-center gap-1.5 rounded-md border border-amber-500/40 px-3 py-2 text-brand-amber hover:bg-amber-500/10 disabled:opacity-40"
            data-testid="snip-highlights-btn">
            {hlJob ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {hlJob ? "Detecting highlights…" : `Find AI highlights · ${costs.highlights} cr`}
          </button>
        </div>
      </div>

      {highlights.length > 0 && (
        <div className="rounded-xl border border-line bg-bg-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="type-section-heading text-ink-primary">AI highlights</h3>
            <span className="type-status-badge font-mono uppercase tracking-wider text-ink-muted">// {highlights.length} MOMENTS</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {highlights.map((h) => (
              <div key={h.hl_id} className="rounded-lg border border-line/60 p-3 flex flex-col" data-testid={`snip-highlight-${h.hl_id}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`type-status-badge rounded-full border px-2 py-0.5 font-mono ${scoreColor(h.score)}`}>
                    <Flame size={9} className="inline mr-0.5 -mt-0.5" />{h.score}
                  </span>
                  <span className="type-list-title text-ink-primary flex-1 min-w-0 truncate">{h.title}</span>
                  <span className="type-timestamp text-ink-muted shrink-0">{fmtTime(h.start)}–{fmtTime(h.end)}</span>
                </div>
                {h.quote && <div className="type-list-subtitle italic text-ink-primary/80 mb-1">“{h.quote}”</div>}
                <div className="type-list-subtitle text-ink-muted mb-2">{h.reason}</div>
                <div className="flex items-center gap-2 mt-auto">
                  {h.emotion && <span className="type-status-badge rounded-full border border-line px-2 py-0.5 text-ink-muted">{h.emotion}</span>}
                  <button onClick={() => createClip({ start: h.start, end: h.end, title: h.title, hl_id: h.hl_id })} disabled={cutting}
                    className="type-button-label ml-auto flex items-center gap-1 rounded-md border border-line px-2.5 py-1 text-ink-primary hover:bg-white/5 disabled:opacity-40"
                    data-testid={`snip-hl-clip-${h.hl_id}`}>
                    {cutting ? <Loader2 size={12} className="animate-spin" /> : <Scissors size={12} />} Create clip
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {transcribed && (
        <div className="rounded-xl border border-line bg-bg-surface p-5">
          <button onClick={() => setShowTranscript((v) => !v)} className="flex w-full items-center gap-2" data-testid="snip-transcript-toggle">
            {showTranscript ? <ChevronDown size={15} className="text-ink-muted" /> : <ChevronRight size={15} className="text-ink-muted" />}
            <h3 className="type-section-heading text-ink-primary">Transcript</h3>
            <span className="type-timestamp text-ink-muted ml-auto">{transcript.length} segments · click a line to load it into the cutter</span>
          </button>
          {showTranscript && (
            <div className="mt-3 max-h-80 overflow-y-auto space-y-0.5" data-testid="snip-transcript-list">
              {transcript.map((s, i) => (
                <button key={i}
                  onClick={() => setCut({ start: s.start, end: s.end, title: "" })}
                  className="flex w-full items-start gap-3 rounded px-2 py-1 text-left hover:bg-white/5">
                  <span className="type-timestamp text-brand-amber/80 w-14 shrink-0 font-mono">{fmtTime(s.start)}</span>
                  <span className="type-body text-ink-primary/90">{s.text}</span>
                </button>
              ))}
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-line/40 pt-3">
            <div>
              <div className="type-card-meta-label text-ink-muted mb-1">Start (sec)</div>
              <input type="number" step="0.5" value={cut.start} onChange={(e) => setCut((p) => ({ ...p, start: e.target.value }))}
                className="w-24 rounded-md border border-line bg-bg-base px-2 py-1.5 type-button-label text-ink-primary" data-testid="snip-cut-start" />
            </div>
            <div>
              <div className="type-card-meta-label text-ink-muted mb-1">End (sec)</div>
              <input type="number" step="0.5" value={cut.end} onChange={(e) => setCut((p) => ({ ...p, end: e.target.value }))}
                className="w-24 rounded-md border border-line bg-bg-base px-2 py-1.5 type-button-label text-ink-primary" data-testid="snip-cut-end" />
            </div>
            <div className="flex-1 min-w-32">
              <div className="type-card-meta-label text-ink-muted mb-1">Clip title (optional)</div>
              <input value={cut.title} onChange={(e) => setCut((p) => ({ ...p, title: e.target.value }))}
                className="w-full rounded-md border border-line bg-bg-base px-2 py-1.5 type-button-label text-ink-primary" data-testid="snip-cut-title" />
            </div>
            <button onClick={() => createClip(cut)} disabled={cutting || cut.start === "" || cut.end === ""}
              className="type-button-label flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-2 text-bg-base disabled:opacity-40"
              data-testid="snip-cut-btn">
              {cutting ? <Loader2 size={13} className="animate-spin" /> : <Scissors size={13} />} Cut clip
            </button>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="type-section-heading text-ink-primary">Clips</h3>
          <span className="type-status-badge font-mono uppercase tracking-wider text-ink-muted">// {clips.length} CLIP{clips.length === 1 ? "" : "S"}</span>
        </div>
        {clips.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line p-6 type-body text-ink-muted" data-testid="snip-clips-empty">
            No clips yet — create one from an AI highlight above, or cut a manual range from the transcript.
          </div>
        ) : (
          <div className="space-y-3">
            {clips.map((c) => (
              <ClipCard key={c.clip_id} clip={c} templates={templates} renderCost={costs.render} onChanged={onClipsChanged} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
